import dayjs from "dayjs";
import { first, last } from "lodash";

export function getColorFromName(name: string) {
  switch (name) {
    case "superb":
      return "#ffe600";
    case "excellent":
      return "#E8A0BF";
    case "good":
      return "#f16622";
    case "fair":
      return "#68cbe2";
    case "project":
      return "#5f84b6";
    case "not_available":
      return "#8297b5";
    default:
      return undefined;
  }
}

export function getColorFromIndex(index: number) {
  if (index >= 0) {
    const shade = Math.abs(9 - index * 2);
    return `yellow.${shade}`;
  }
  return "yellow";
}

export function getDataRange(data: Record<string, any>[]): [number, number] {
  if (!data || data.length === 0) return [0, 0];

  const parseDate = (value: any) => {
    if (!value) return dayjs();
    // try parsing as year-month first (e.g. "2026-02"), fall back to generic parse
    const asYearMonth = dayjs(String(value), "YYYY-MM", true);
    return asYearMonth.isValid() ? asYearMonth : dayjs(String(value));
  };

  return [parseDate(first(data)?.date).unix(), parseDate(last(data)?.date).unix()];
}

export function getDateFormatter(range: [number, number]) {
  const start = dayjs.unix(range?.[0] ?? 0);
  const end = dayjs.unix(range?.[1] ?? 0);

  if (!start.isValid() || !end.isValid()) {
    return (value: string) => String(value);
  }

  const daysDiff = end.diff(start, "day");
  const monthsDiff = end.diff(start, "month");

  let format = "YYYY";
  // Prefer month/year when range stays within a single month (covers YYYY-MM inputs)
  if (start.isSame(end, "month")) {
    format = "MMM YYYY";
  } else if (start.isSame(end, "day")) {
    // very short ranges (same day)
    format = "HH:mm";
  } else if (daysDiff <= 30) {
    format = "DD MMM";
  } else if (monthsDiff <= 12) {
    format = "MMM YYYY";
  }

  return (value: string) => {
    // support inputs like "YYYY-MM" or full date strings
    const parsed = dayjs(String(value), "YYYY-MM", true);
    return (parsed.isValid() ? parsed : dayjs(String(value))).format(format);
  };
}

export function getUniqueTicks(ticks: string[], formatter: (value: string) => string) {
  const uniqueTicks: string[] = [];
  const formattedTicks: string[] = [];

  ticks.forEach((tick) => {
    const formattedTick = formatter(tick);
    if (!formattedTicks.includes(formattedTick)) {
      formattedTicks.push(formattedTick);
      uniqueTicks.push(tick);
    }
  });

  return uniqueTicks;
}
